import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, QrCode, FileText, History } from "lucide-react";
import MachineMockup from "./MachineMockup";

export default function DemoSection() {
  const [hover, setHover] = useState(false);
  const [playing, setPlaying] = useState(false);
  const navigate = useNavigate();

  const chip = (show) => ({
    opacity: show ? 1 : 0,
    y: show ? 0 : 10,
  });

  return (
    <section className="border-b border-zinc-100" data-testid="demo-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-24 lg:py-36">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs uppercase tracking-widest font-medium text-zinc-500 mb-5">
            Ver Asset Passport en acción
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-zinc-900 leading-tight">
            De la máquina a su información. En segundos.
          </h2>
          <p className="text-lg text-zinc-600 leading-relaxed mt-5 max-w-2xl mx-auto">
            Escanea el QR de una máquina, accede a su pasaporte y encuentra documentación,
            fotografías e historial exactamente donde los necesitas.
          </p>
          <div className="mt-8">
            <button
              onClick={() => navigate("/demo")}
              data-testid="demo-cta-button"
              className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-zinc-900 text-white font-medium hover:bg-zinc-800 hover:scale-[1.02] active:scale-95 transition-[background-color,transform] duration-200"
            >
              <Play size={16} className="fill-current" />
              Ver demo
            </button>
            <p className="text-sm text-zinc-400 mt-4">
              Descubre el flujo completo en 1 minuto.
            </p>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-4xl mx-auto mt-14 lg:mt-20"
        >
          <div
            role="link"
            tabIndex={0}
            onClick={() => setPlaying(true)}
            onKeyDown={(e) => e.key === "Enter" && setPlaying(true)}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            data-testid="demo-preview"
            className="relative rounded-3xl border border-zinc-200/90 bg-white shadow-[0_24px_70px_-20px_rgba(0,0,0,0.15)] overflow-hidden cursor-pointer group focus-visible:ring-2 focus-visible:ring-zinc-950 focus-visible:ring-offset-2"
          >
            <div className="flex items-center gap-1.5 px-5 py-3.5 border-b border-zinc-100">
              <span className="h-2.5 w-2.5 rounded-full bg-zinc-200" />
              <span className="h-2.5 w-2.5 rounded-full bg-zinc-200" />
              <span className="h-2.5 w-2.5 rounded-full bg-zinc-200" />
              <span className="ml-3 text-xs text-zinc-400 font-medium">Asset Passport</span>
            </div>

            {playing ? (
              <div className="relative aspect-video bg-zinc-950">
                <video
                  className="absolute inset-0 h-full w-full"
                  src="/demo/demo_v2.mp4"
                  poster="/demo/poster.jpg"
                  controls
                  autoPlay
                  playsInline
                  data-testid="demo-preview-video"
                />
              </div>
            ) : (
            <div className="relative bg-zinc-50/70 px-6 py-10 sm:px-14 sm:py-14">
              <motion.div
                animate={{ scale: hover ? 1.012 : 1 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className="max-w-sm mx-auto pointer-events-none"
              >
                <MachineMockup compact testid="demo-preview-mockup" testidPrefix="demo-prev-" />
              </motion.div>

              <motion.div
                animate={chip(hover)}
                transition={{ duration: 0.35 }}
                className="hidden sm:flex absolute left-6 lg:left-10 top-8 items-center gap-2.5 px-4 py-2.5 rounded-full bg-white border border-zinc-200 shadow-sm"
                data-testid="demo-preview-qr"
              >
                <span className="h-8 w-8 rounded-lg bg-zinc-900 text-white flex items-center justify-center">
                  <QrCode size={15} />
                </span>
                <span className="text-xs font-medium text-zinc-700">Escanea</span>
              </motion.div>

              <motion.div
                animate={chip(hover)}
                transition={{ duration: 0.35, delay: 0.08 }}
                className="hidden sm:flex absolute right-6 lg:right-10 top-1/2 -translate-y-1/2 items-center gap-2.5 px-4 py-2.5 rounded-full bg-white border border-zinc-200 shadow-sm"
                data-testid="demo-preview-doc"
              >
                <span className="h-8 w-8 rounded-lg bg-zinc-100 text-zinc-500 flex items-center justify-center">
                  <FileText size={15} />
                </span>
                <span className="text-xs font-medium text-zinc-700">Esquema eléctrico.pdf</span>
              </motion.div>

              <motion.div
                animate={chip(hover)}
                transition={{ duration: 0.35, delay: 0.16 }}
                className="hidden sm:flex absolute left-8 lg:left-14 bottom-8 items-center gap-2.5 px-4 py-2.5 rounded-full bg-white border border-zinc-200 shadow-sm"
                data-testid="demo-preview-history"
              >
                <span className="h-8 w-8 rounded-lg bg-zinc-100 text-zinc-500 flex items-center justify-center">
                  <History size={15} />
                </span>
                <span className="text-xs font-medium text-zinc-700">Cambio de filtros · 14 ago</span>
              </motion.div>

              <motion.div
                animate={chip(hover)}
                transition={{ duration: 0.35, delay: 0.24 }}
                className="hidden sm:block absolute right-8 lg:right-14 bottom-6 w-16 rounded-2xl border border-zinc-300 bg-zinc-950 p-1 shadow-lg"
                data-testid="demo-preview-phone"
              >
                <div className="rounded-xl bg-white p-1.5">
                  <div className="h-6 rounded-md bg-zinc-100 mb-1" />
                  <div className="space-y-0.5">
                    <div className="h-1.5 rounded bg-zinc-200" />
                    <div className="h-1.5 rounded bg-zinc-100" />
                    <div className="h-1.5 rounded bg-zinc-100" />
                  </div>
                </div>
              </motion.div>

              <div className="absolute inset-0 flex items-center justify-center">
                <motion.span
                  animate={{ scale: hover ? 1.1 : 1 }}
                  transition={{ duration: 0.3 }}
                  className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-zinc-900/90 backdrop-blur text-white flex items-center justify-center shadow-2xl"
                  data-testid="demo-preview-play"
                >
                  <Play size={26} className="fill-current ml-1" />
                </motion.span>
              </div>
            </div>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
